import { useState, useCallback, useRef, useMemo } from "react";
import { useTourGuideContext } from "@root/context/tour-guide-context";
import { TourStep } from "./useTourGuide";

export interface UseTourGuideHoverReturn {
  isHovered: boolean;
  hoverProps: {
    onMouseEnter: () => void;
    onMouseLeave: () => void;
  };
  tooltipData: {
    title: string;
    description: string;
    position?: "top" | "bottom" | "left" | "right" | "center";
  } | null;
}

export const useTourGuideHover = (
  stepId: string,
  stepData?: TourStep
): UseTourGuideHoverReturn => {
  const [isHovered, setIsHovered] = useState(false);
  const leaveTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  
  const {
    hoverTooltipsEnabled,
    disabledHoverSteps,
    setHoveredStepId,
  } = useTourGuideContext();

  const isStepDisabled = disabledHoverSteps.has(stepId);

  const handleMouseEnter = useCallback(() => {
    if (!hoverTooltipsEnabled || isStepDisabled) {
      return;
    }

    if (leaveTimeoutRef.current) {
      clearTimeout(leaveTimeoutRef.current);
      leaveTimeoutRef.current = null;
    }

    setIsHovered(true);
    setHoveredStepId(stepId);
  }, [hoverTooltipsEnabled, isStepDisabled, stepId, setHoveredStepId]);

  const handleMouseLeave = useCallback(() => {
    if (!hoverTooltipsEnabled || isStepDisabled) {
      return;
    }

    // Add delay to prevent flicker
    leaveTimeoutRef.current = setTimeout(() => {
      setIsHovered(false);
      setHoveredStepId(null);
    }, 100);
  }, [hoverTooltipsEnabled, isStepDisabled, setHoveredStepId]);

  const hoverProps = useMemo(
    () => ({
      onMouseEnter: handleMouseEnter,
      onMouseLeave: handleMouseLeave,
    }),
    [handleMouseEnter, handleMouseLeave]
  );

  const tooltipData = useMemo(() => {
    if (!stepData) {
      return null;
    }

    return {
      title: stepData.title,
      description: stepData.description,
      position: stepData.position,
    };
  }, [stepData]);

  return {
    isHovered,
    hoverProps,
    tooltipData,
  };
};
